// src/pages/finance-portal/budgets/BudgetVariance.js
//
// Variance Waterfall: Budget vs Actual GL postings.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, IconButton, Card, Chip, Alert, Skeleton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RemoveIcon from '@mui/icons-material/Remove';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

function fmtMoney(val) {
    if (val == null || isNaN(val)) return '—';
    const n = Number(val);
    const abs = Math.abs(n);
    return (n < 0 ? '-' : '') + new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', maximumFractionDigits: 0,
    }).format(abs);
}

function fmtPct(val) {
    if (val == null || isNaN(val)) return '—';
    const n = Number(val);
    return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
}

export default function BudgetVariance() {
    const { budgetId } = useParams();
    const navigate = useNavigate();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/budgets/${budgetId}/variance`);
            if (res.data.success) {
                setReport(res.data);
            } else {
                setError(res.data.message || 'Failed to load variance');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load variance');
        } finally {
            setLoading(false);
        }
    }, [budgetId]);

    useEffect(() => { fetchReport(); }, [fetchReport]);

    const labelCell = { fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b',
                        textTransform: 'uppercase', bgcolor: '#f8fafc',
                        borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' };
    const dataCell = { fontFamily, fontSize: '0.85rem', color: '#475569' };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3, position: 'relative',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate(`/finance-portal/budgets/${budgetId}`)}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Budget Variance Report
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            Budget vs Actual
                        </Typography>
                        {report && (
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)',
                                              fontSize: '0.8rem', mt: 0.25 }}>
                                {report.period_start} → {report.period_end}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {loading ? (
                    <Skeleton height={400} />
                ) : !report ? null : (
                    <>
                        <Box sx={{ display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                  gap: 2, mb: 3 }}>
                            {[
                                { label: 'Total Budget', value: report.totals.budget, color: '#3b82f6' },
                                { label: 'Total Actual', value: report.totals.actual, color: '#10b981' },
                                { label: 'Variance', value: report.totals.variance,
                                  color: report.totals.variance >= 0 ? '#ef4444' : '#16a34a' },
                            ].map(card => (
                                <Card key={card.label} sx={{ borderRadius: '14px', p: 2.5, boxShadow: 'none',
                                                              border: '1px solid #e2e8f0' }}>
                                    <Typography sx={{ fontFamily, color: '#64748b',
                                                      fontSize: '0.78rem', fontWeight: 600,
                                                      textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        {card.label}
                                    </Typography>
                                    <Typography sx={{ fontFamily, fontWeight: 800, fontSize: '1.6rem',
                                                       color: card.color, mt: 0.5 }}>
                                        {fmtMoney(card.value)}
                                    </Typography>
                                </Card>
                            ))}
                        </Box>

                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0' }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem' }}>
                                    Per-Account Variance
                                </Typography>
                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.78rem', mt: 0.25 }}>
                                    Positive variance for expense accounts means over-budget. Color-coded by favorability.
                                </Typography>
                            </Box>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {['Account', 'Type', 'Budget', 'Actual', 'Variance', 'Variance %', ''].map(h =>
                                                <TableCell key={h} sx={{ ...labelCell,
                                                                          textAlign: ['Budget','Actual','Variance','Variance %'].includes(h) ? 'right' : 'left' }}>
                                                    {h}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {report.rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} sx={{ textAlign: 'center', py: 6 }}>
                                                    <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.95rem' }}>
                                                        No data — neither budget lines nor actuals in this period.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : report.rows.map(r => {
                                            const Icon = r.is_favorable ? TrendingDownIcon
                                                : (Math.abs(r.variance) < 0.01 ? RemoveIcon : TrendingUpIcon);
                                            const tone = Math.abs(r.variance) < 0.01 ? '#94a3b8'
                                                : (r.is_favorable ? '#16a34a' : '#ef4444');
                                            return (
                                                <TableRow key={r.gl_account_id} hover>
                                                    <TableCell sx={dataCell}>
                                                        <Box>
                                                            <Typography sx={{ fontFamily, fontWeight: 600,
                                                                              color: '#1e293b', fontSize: '0.85rem' }}>
                                                                {r.account_number} — {r.account_name}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, fontSize: '0.78rem' }}>
                                                        <Chip label={r.account_type || '—'} size="small"
                                                              sx={{ fontFamily, fontSize: '0.7rem',
                                                                    height: 20, bgcolor: '#f1f5f9', color: '#475569' }} />
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                        {fmtMoney(r.budget)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right' }}>
                                                        {fmtMoney(r.actual)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                                      fontWeight: 700, color: tone }}>
                                                        {fmtMoney(r.variance)}
                                                    </TableCell>
                                                    <TableCell sx={{ ...dataCell, textAlign: 'right',
                                                                      fontWeight: 600, color: tone }}>
                                                        {fmtPct(r.variance_pct)}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title={r.is_favorable ? 'Favorable' : 'Unfavorable'}>
                                                            <Icon sx={{ color: tone, fontSize: 18 }} />
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Card>
                    </>
                )}
            </Box>
        </Box>
    );
}
